import { useEffect, useState } from "react";
import { ShieldWarning, CheckCircle } from "@phosphor-icons/react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api, shortDate, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const LEVEL_CLS = {
  low: "bg-emerald-50 text-emerald-700 border-emerald-300",
  medium: "bg-amber-50 text-amber-800 border-amber-300",
  high: "bg-alert/10 text-alert border-alert/40",
};
const RATING = [["1", "1 · Very Low"], ["2", "2 · Low"], ["3", "3 · Moderate"], ["4", "4 · High"], ["5", "5 · Very High"]];

function levelFor(avg) {
  if (avg >= 3.5) return "high";
  if (avg >= 2.5) return "medium";
  return "low";
}

export function RiskAssessmentTab({ grant, canManage, reload }) {
  const [factors, setFactors] = useState([]);
  const [history, setHistory] = useState([]);
  const existing = grant.risk_assessment || {};
  const [values, setValues] = useState(existing.factors || {});
  const [notes, setNotes] = useState(existing.notes || "");

  useEffect(() => {
    api.get("/risk-factors").then((r) => setFactors(r.data)).catch(() => setFactors([]));
  }, []);
  const loadHistory = () => api.get(`/grants/${grant.id}/risk-history`).then((r) => setHistory(r.data)).catch(() => setHistory([]));
  useEffect(() => {
    setValues(grant.risk_assessment?.factors || {});
    setNotes(grant.risk_assessment?.notes || "");
    loadHistory();
    // eslint-disable-next-line
  }, [grant.risk_assessment, grant.id]);

  const scores = factors.map((f) => Number(values[f.key]) || 0).filter((s) => s > 0);
  const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
  const level = levelFor(avg);

  const save = async () => {
    try {
      await api.put(`/grants/${grant.id}/risk-assessment`, { factors: values, notes });
      toast.success("Risk assessment saved");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-card border border-border rounded-md p-6">
        <div className="flex items-center gap-2 mb-1"><ShieldWarning size={20} className="text-primary" /><span className="font-head font-semibold">Risk Assessment</span></div>
        <p className="text-sm text-muted-foreground mb-5">Rate each factor 1 (very low risk) to 5 (very high risk). The score rolls up to a risk level and compliance status.</p>
        <div className="space-y-4">
          {factors.map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-4 border-b border-border pb-3">
              <span className="text-sm">{f.label}</span>
              <Select value={String(values[f.key] || "")} onValueChange={(v) => setValues({ ...values, [f.key]: Number(v) })} disabled={!canManage}>
                <SelectTrigger data-testid={`risk-${f.key}`} className="w-40"><SelectValue placeholder="Rate…" /></SelectTrigger>
                <SelectContent>{RATING.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-1.5">
          <Label className="overline text-muted-foreground">Assessor notes</Label>
          <Textarea data-testid="risk-notes" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canManage} />
        </div>
        {canManage && <div className="mt-5"><Button data-testid="risk-save" onClick={save}>Save Assessment</Button></div>}
      </div>

      <div className="bg-card border border-border rounded-md p-6">
        <div className="overline text-muted-foreground mb-3">Rolled-up Risk</div>
        <div className="text-center py-4">
          <div className="font-head font-bold text-5xl text-foreground">{avg ? avg.toFixed(1) : "—"}</div>
          <div className="text-xs text-muted-foreground mt-1">Composite score (1–5)</div>
          <span data-testid="risk-level-badge" className={`inline-flex mt-4 px-3 py-1 rounded-sm border text-sm font-medium capitalize ${LEVEL_CLS[level]}`}>{level} risk</span>
        </div>
        <div className="border-t border-border mt-4 pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Current compliance</span><span className="font-medium capitalize">{(grant.compliance_status || "").replace(/_/g, " ")}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Saved level</span><span className="font-medium capitalize">{grant.risk_level || "—"}</span></div>
          {existing.assessed_by && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1"><CheckCircle size={13} /> {existing.assessed_by} · {shortDate(existing.assessed_at)}</div>
          )}
        </div>

        <div className="border-t border-border mt-4 pt-4" data-testid="risk-history">
          <div className="overline text-muted-foreground mb-2">Risk History</div>
          {history.length === 0 ? (
            <p className="text-xs text-muted-foreground">No prior assessments.</p>
          ) : (
            <>
              {history.length > 1 && (
                <ResponsiveContainer width="100%" height={110}>
                  <LineChart data={history.map((h) => ({ date: (h.date || "").slice(5), score: h.score }))} margin={{ top: 6, right: 6, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                    <YAxis domain={[0, 5]} tick={{ fontSize: 9 }} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                    <Line type="monotone" dataKey="score" stroke="#bf5027" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
              <div className="space-y-1.5 mt-2 max-h-40 overflow-y-auto">
                {[...history].reverse().map((h, i) => (
                  <div key={i} data-testid={`risk-hist-${i}`} className="flex items-center justify-between text-xs border-b border-border/60 pb-1.5">
                    <span className="text-muted-foreground">{shortDate(h.assessed_at)} · {h.assessed_by}</span>
                    <span className="font-medium">{h.score} <span className="capitalize text-muted-foreground">({h.level})</span></span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
